import { getInfo, getRouters, loginout } from '@/api/auth'
import { getToken, setToken, removeToken } from '@/utils/auth'
import { showDesktopNotification } from '@/utils/notification'
import { constantRoutes } from '@/router'
import Layout from '@/layout/index'
import { EventBus } from "@/utils/eventBus.js";
import WebSocketService from '@/api/webSocketService';
import ling from '@/assets/ling.wav'

const state = {
  token: getToken(),
  userInfo: null,
  routes: [],
  role: '',
  wsService: null,    
  unreadMessages: {}, // 未读消息对象
  totalUnread: 0,
  titleInterval: null, // 标题闪烁定时器
  notificationAudio: new Audio(ling),
  isPlaying: false, // 避免重复播放
  orderInfo: {
    remainingDays: 1
  }
}

const mutations = {
  SET_TOKEN: (state, token) => {
    state.token = token
  },
  SET_USERINFO: (state, info) => {
    state.userInfo = info
  },
  SET_ROLE: (state, role) => {
    state.role = role
  },
  SET_ROUTERS: (state, routes) => {
    state.routes = constantRoutes.concat(routes)
  },
  SET_LOGOUT: (state) => {
    state.token = ''
    state.userInfo = null
    state.role = ''
    state.titleInterval && clearInterval(state.titleInterval);
    state.titleInterval = null;
    // document.title = "DomiDesk-在线客服系统";
    document.title = "银和数界";
    state.totalUnread = 0;
    state.unreadMessages = {};
  },
  SET_WS_SERVICE(state, wsService) {
    state.wsService = wsService;
  },
  // 指定对话 ID，增加未读消息
  INCREMENT_UNREAD(state, talkId) {
    if (!state.unreadMessages[talkId]) {
      state.unreadMessages[talkId] = 0;
    }
    state.unreadMessages[talkId]++;
    state.totalUnread++;
  },
  // 指定对话 ID，清除未读
  RESET_UNREAD(state, talkId) {
    if (state.unreadMessages[talkId]) {
      state.totalUnread -= state.unreadMessages[talkId];
      state.unreadMessages[talkId] = 0;
    }
  },
  RESET_ALL_UNREAD(state) {
    state.unreadMessages = {};
    state.totalUnread = 0;
  },
  // 消息闪烁
  START_TITLE_FLASH(state, message) {
    showDesktopNotification(message.type === 'EXTEND' || message.type === 'AI_EXTEND' ? message.extend : message.text);
    window.trayAPI?.startFlash();
    if (state.titleInterval) return; 
    state.titleInterval = setInterval(() => {
      document.title = document.title === "🔔 新消息" ? "银和数界" : "🔔 新消息";
    }, 800);
  },
  // 停止闪烁
  STOP_TITLE_FLASH(state) {
    if (state.titleInterval) {
      clearInterval(state.titleInterval);
      state.titleInterval = null;
    }
    document.title = "银和数界";
  },
}

const actions = {
  Login({ commit }, token) {
    commit('SET_TOKEN', token)
    setToken(token)
  },

  GetInfo({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      getInfo().then(response => {
        const { data } = response;
        commit('SET_USERINFO', data)
        commit('SET_ROLE', data.roleName)
        // dispatch('ConnectWebSocket');
        resolve(data)
      }).catch(error => {
        reject(error)
      })
    })
  },

  GenerateRoutes({ commit, state }) {
    return new Promise((resolve, reject) => {
      getRouters().then(res => {
        let result = res.data || [];
        const accessRoutes = filterAsyncRouter(JSON.parse(JSON.stringify(result)), false, true);
        accessRoutes.push({ path: '*', redirect: '/404', hidden: true })
        accessRoutes.push({ path: '*', redirect: '/noPermission', hidden: true })
        let sidebarRoutes = filterAsyncRouter(JSON.parse(JSON.stringify(result)));
        const geoState = state.userInfo ? state.userInfo.geoState : undefined;
        if (geoState !== 1 && geoState !== '1') {
          sidebarRoutes = filterGeoRoutes(sidebarRoutes);
        }
        commit('SET_ROUTERS', sidebarRoutes)
        resolve(accessRoutes)
      }).catch(error => {
        reject(error)
      })
    })
  },

  ExpireSession({ commit, state }) {
    return new Promise((resolve, reject) => {
      commit('SET_LOGOUT');
      removeToken();
      if (state.wsService) {
        state.wsService.close(); 
      }
      resolve()
    })
  },

  LogOut({ commit, state }) {
    return new Promise((resolve, reject) => {
      loginout().then((res) => {
        commit('SET_LOGOUT');
        removeToken();
        if (state.wsService) {
          state.wsService.close(); 
        }
        resolve()
      }).catch((err) => {
        reject(err)
      })
    })
  },
  // 连接 WebSocket
  ConnectWebSocket({ commit, state, dispatch }) {
    if (state.wsService) {
      return; 
    }
    const wsService = new WebSocketService();
    wsService.connect();
    wsService.setHandlers({
      onOpen: () => {
        wsService.send({
          type: 'CONNECT_USER',
          userType: 2,
          companyId: state.userInfo?.companyId,
          sendId: state.userInfo?.id,
          token: state.token
        })
      },
      onMessage: (data) => {
        let message = {}
        try {
          message = JSON.parse(data)
        } catch (error) {
          
        }
        if(message.type === 'MSG' || message.type === 'AI_BOT_REPLY' || message.type === 'AUTO' || message.type === 'AUTO_REPLY' || message.type === 'EXTEND' || message.type === 'AI_EXTEND' || message.type === 'SYSTEM' || message.type === 'MSG_QUOTE') {
          if(message.userType === 1 && message.type !== 'SYSTEM') {
            dispatch('HandleNotification', message);
          }
          EventBus.$emit('newMessage', {
            type: 'success',
            message
          });
        }
      },
      onSendError: (message) => {
        console.error('WebSocket 未连接，无法发送消息');
        EventBus.$emit('newMessage', {
          type: 'error',
          message
        });
      }
    });

    wsService.connect();
    commit("SET_WS_SERVICE", wsService);
  },
  HandleNotification({ commit, state }, message) {
    commit("INCREMENT_UNREAD", message.talkId);

    // 开启标题闪烁
    if(state.userInfo?.cueHead) {
      commit("START_TITLE_FLASH", message); 
    }
    
    // 开启播放声音
    if(state.userInfo?.cueVoice) {
      if (!state.isPlaying) {
        state.isPlaying = true;
        state.notificationAudio.currentTime = 0; 
        state.notificationAudio
          .play()
          .catch((e) => console.log("音频播放被浏览器限制", e))
          .finally(() => {
            setTimeout(() => {
              state.isPlaying = false; 
            }, 1000); 
          });
      }
    }
  },
  StopFlash({ commit }) {
    commit('STOP_TITLE_FLASH')
  }
}

// 遍历后台传来的路由字符串，转换为组件对象
function filterAsyncRouter(asyncRouterMap, lastRouter = false, type = false) {
  return asyncRouterMap.filter(route => {
    if (type && route.children) {
      route.children = filterChildren(route.children)
    }
    if (route.component) {
      // Layout ParentView 组件特殊处理
      if (route.component === 'Layout') {
        route.component = Layout
      } else {
        route.component = loadView(route.component)
      }
    }
    if (route.children != null && route.children && route.children.length) {
      route.children = filterAsyncRouter(route.children, route, type)
    } else {
      delete route['children']
      delete route['redirect']
    }
    return true
  })
}

function filterChildren(childrenMap, lastRouter = false) {
  var children = []
  childrenMap.forEach((el) => {
    if (el.children && el.children.length) {
      if (el.component === 'ParentView' && !lastRouter) {
        el.children.forEach(c => {
          c.path = el.path + '/' + c.path
          if (c.children && c.children.length) {
            children = children.concat(filterChildren(c.children, c))
            return
          }
          children.push(c)
        })
        return
      }
    }
    if (lastRouter) {
      el.path = lastRouter.path + '/' + el.path
      if (el.children && el.children.length) {
        children = children.concat(filterChildren(el.children, el))
        return
      }
    }
    children = children.concat(el)
  })
  return children
}

// 递归过滤GEO路由
function filterGeoRoutes(routes) {
  return routes.filter(route => {
    const isGeo = (route.path && route.path.toLowerCase().indexOf('geo') !== -1) ||
                  (route.name && route.name.toLowerCase().indexOf('geo') !== -1);
    if (isGeo) return false;
    if (route.children && route.children.length) {
      route.children = filterGeoRoutes(route.children);
    }
    return true;
  });
}

export const loadView = (view) => {
  if (process.env.NODE_ENV === 'development') {
    return (resolve) => require([`@/views/${view}`], resolve)
  } else {
    return () => import(`@/views/${view}`)
  }
};



export default {
  state,
  mutations,
  actions
}
